/**
 * Login as Partner, on the browser side.
 *
 * The partner-scoped token lives in sessionStorage, not localStorage: it belongs to this one tab.
 * The staff member's own session in localStorage is never touched, so every other tab stays signed
 * in as them, and closing this tab simply abandons the partner session (the server ends it after
 * 30 minutes regardless).
 */
const KEY = 'isp_impersonation';

export interface ImpersonationState {
  token: string;
  sessionId: string;
  expiresAt: string;
  partner: { id: string; name: string; code: string };
  partnerUser: { id: string; name: string; role: string };
  /** Where "End session" takes the staff member back to. */
  returnTo: string;
}

function read(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as ImpersonationState;
    return s && typeof s.token === 'string' && typeof s.expiresAt === 'string' ? s : null;
  } catch {
    return null;
  }
}

/** The live session for this tab, or null. An expired one is reported as null. */
export function getImpersonation(): ImpersonationState | null {
  const s = read();
  if (!s) return null;
  return new Date(s.expiresAt).getTime() > Date.now() ? s : null;
}

/** The stored session even if it has run out — used to send the staff member back where they came from. */
export function getStoredImpersonation(): ImpersonationState | null {
  return read();
}

export function beginImpersonation(state: ImpersonationState): void {
  sessionStorage.setItem(KEY, JSON.stringify(state));
  // A full load, so nothing cached from the staff session (queries, permissions, menus) survives.
  window.location.assign('/partner-portal');
}

export function clearImpersonation(): ImpersonationState | null {
  const prev = read();
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
  return prev;
}
